import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import chevronDownIcon from '@/assets/svg/chevron-down-icon.svg';
import ErrorDialog from '@/components/shared/errorDialog';
import { MobiTitle } from '@/components/shared/MobiTitle';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import type { VehicleDriverOptionType, VehicleDriverRes } from '@/features/vehicle-driver/types/vehicleDriver';
import { useCreate } from '@/modules/hooks/use-create';
import { apiGet } from '@/modules/hooks/use-query';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';
import { ServiceType } from '@/types/serviceType';

export const SelectPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [setting] = useRecoilState(settingState);

  // ドライバID（cognitoのdriverId）
  const driverId = setting?.driverId || '';

  const [vehicleDriverOptions, setVehicleDriverOptions] = useState<VehicleDriverOptionType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ドライバ管理会社ID
  const serviceType = useServiceType();

  // エラーダイアログ
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [vehicleDriverSelectError, setVehicleDriverSelectError] = useState('');

  const [disabledFlg, setDisabledFlg] = useState<boolean>(false);

  useEffect(() => {
    // 定時定路線・リアルタイムオンデマンドの場合
    if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
      setDisabledFlg(false);
    }
    // 事前予約の場合
    if (serviceType === ServiceType.PreBooking) {
      setDisabledFlg(true);
    }
  }, [serviceType]);

  useEffect(() => {
    if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
      // 車両・ドライバ一覧取得API
      apiGet(ApiNamePaths.vehicleDriverApi.apiName, ApiNamePaths.vehicleDriverApi.apiPath, {})
        .then((res) => {
          const data = res as VehicleDriverRes;
          const vehicleDriverOptionList =
            data?.vehicleDriverList?.map((v) => ({
              vehicleDriverInfo: `${v.vehicleRegionName ?? ''} ${v.numberPlateClassCode ?? ''} ${
                v.numberPlateHiraganaEtc ?? ''
              } ${(v.numberPlateNumber ?? '').replace(/(\d{2})(\d{2})/, '$1-$2')}`, // {車両地名} {ナンバー（分類番号）} {ナンバー（平仮名等）} {ナンバー（番号）}
              vehicleId: v.vehicleId,
              driverName: `${v.lastName ?? ''} ${v.firstName ?? ''}`,
              isSelected: v.isSelected,
              lastName: v.lastName,
              firstName: v.firstName,
              lastNameHiragana: v.lastNameHiragana,
              firstNameHiragana: v.firstNameHiragana,
            })) ?? [];

          setVehicleDriverOptions(vehicleDriverOptionList);
          setInputValue('');
          setModalShow(false);
        })
        .catch((error) => {
          setVehicleDriverSelectError(error instanceof Error ? error?.message : '');
          setModalShow(true);
        });
    }
  }, [serviceType]);

  const { mutate } = useCreate(
    queryKeys.v2RshVehicleDriver,
    ApiNamePaths.vehicleDriverApi.apiName,
    ApiNamePaths.vehicleDriverApi.apiPath,
  );

  const submitSelection = (targetOption: VehicleDriverOptionType) => {
    if (!targetOption || isSubmitting || disabledFlg) {
      return;
    }

    setIsSubmitting(true);
    mutate(
      {
        driverId,
        vehicleId: targetOption.vehicleId,
      },
      {
        onSuccess: () => {
          setModalShow(false);
          sessionStorage.setItem(StorageKeys.VEHICLE_ID, targetOption.vehicleId?.toString());
          navigate(paths.driveList);
        },
        onError: (error) => {
          setVehicleDriverSelectError(error instanceof Error ? error.message : '');
          setModalShow(true);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  const filterOptions = useCallback(
    (options: VehicleDriverOptionType[], { inputValue: inputValueTemp }: { inputValue: string }) => {
      const formattedInput =
        inputValueTemp.length === 4 ? inputValueTemp.replace(/(\d{2})(\d{2})/, '$1-$2') : inputValueTemp;

      // ひらがなや漢字でドライバを検索できるように検索可能文字列を設定する
      const formattedNameInput = inputValueTemp.replace(/ /g, '');

      return options.filter(
        (option) =>
          ((option.lastName ?? '') + (option.firstName ?? '')).includes(formattedNameInput) || // 姓 名
          ((option.lastNameHiragana ?? '') + (option.firstNameHiragana ?? '')).includes(formattedNameInput) || // 姓（ひらがな） 名（ひらがな）
          option.vehicleDriverInfo.includes(formattedInput) ||
          option.driverName.includes(formattedInput) ||
          option.vehicleDriverInfo.includes(inputValueTemp) ||
          option.driverName.includes(inputValueTemp),
      );
    },
    [],
  );

  const filteredOptions = useMemo(
    () => filterOptions(vehicleDriverOptions, { inputValue }),
    [inputValue, vehicleDriverOptions, filterOptions],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (!disabledFlg) {
      setIsDropdownOpen(true);
    }
  };

  const handleOptionSelect = (option: VehicleDriverOptionType) => {
    setInputValue(option.vehicleDriverInfo);
    setIsDropdownOpen(false);
    submitSelection(option);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div className="selection-page">
      <MobiTitle>{t('vehicleDriver.titleOfSelect')}</MobiTitle>
      <div className="screen-shell">
        <div className="content-stack">
          <h1 className="screen-title">{t('vehicleDriver.selectTitle')}</h1>

          <div className="selection-block" ref={containerRef}>
            <div className={`selection-control ${disabledFlg ? 'is-disabled' : ''}`}>
              <input
                className="selection-input"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                  if (!disabledFlg) {
                    setIsDropdownOpen(true);
                  }
                }}
                placeholder=""
                disabled={disabledFlg || isSubmitting}
                aria-label={t('vehicleDriver.selectTitle')}
              />
              <button
                className="icon-toggle-button u-disabled-cursor"
                type="button"
                onClick={() => {
                  if (!disabledFlg) {
                    setIsDropdownOpen((prev) => !prev);
                  }
                }}
                disabled={disabledFlg || isSubmitting}
                aria-label="toggle options"
              >
                <img
                  src={chevronDownIcon}
                  alt=""
                  className={`icon-chevron ${isDropdownOpen ? 'is-open' : ''}`}
                  aria-hidden
                />
              </button>
            </div>

            {isDropdownOpen && filteredOptions.length > 0 && (
              <ul className="option-list">
                {filteredOptions.slice(0, 30).map((option) => (
                  <li key={option.vehicleId} className={`option-item ${option.isSelected ? 'is-locked' : ''}`}>
                    <button
                      className="option-button u-disabled-cursor"
                      type="button"
                      disabled={option.isSelected || isSubmitting}
                      onClick={() => {
                        handleOptionSelect(option);
                      }}
                    >
                      <span className="option-label">{option.vehicleDriverInfo}</span>
                      {option.isSelected && (
                        <span className="option-meta">
                          {t('vehicleDriver.drive')}
                          {option.driverName}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {modalShow && (
        <ErrorDialog
          open={modalShow}
          errorMessage={vehicleDriverSelectError}
          onClose={(flag) => {
            setModalShow(flag);
          }}
        />
      )}
    </div>
  );
};
